import React, { Fragment, useMemo } from 'react';

// DEPENDENCIES
import orderBy from 'lodash/orderBy';
import sumBy from 'lodash/sumBy';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';

// CONTEXT
import AMFIProductsProvider, { useAMFIProducts } from '../../../../forms/helpers/AMFIProductsProvider';

// LOCAL COMPONENTS
import FormRow from '../../../../forms/tools/FormRow';
import FormCol from '../../../../forms/tools/FormCol';

// STYLES
import styles from './amfiProducts.module.scss';

// MAIN COMPONENT
const AMFIProductsForm = () => {
  const {
    amGrossTime,
    amProducts,
    amProductsTotalGross,
    aryDealerAMFinance,
    fiProducts,
    fiProductsTotalGross,
    fmGrossTime,
    totalBackendGross
  } = useAMFIProducts();

  const amTotalSell = useMemo(() => {
    return sumBy(aryDealerAMFinance || [], p => +p?.item_selling_price);
  }, [aryDealerAMFinance]);

  const orderedAmProducts = useMemo(() => {
    return orderBy(
      amProducts || [],
      ['product_type', 'product_code', 'item_name'],
      ['asc', 'asc', 'asc']
    );
  }, [amProducts]);

  const orderedFiProducts = useMemo(() => {
    return orderBy(
      fiProducts || [],
      ['product_type', 'product_code', 'item_name'],
      ['asc', 'asc', 'asc']
    );
  }, [fiProducts]);

  // RENDER
  return (
    <Form.Body className={styles.form} plaintext>
      {amTotalSell !== 0 && (
        <Form.Col>
          <FormRow className="d-none d-md-flex">
            <FormCol cols={{ xs: 24, md: 8 }} className={styles.labelCol}>
              <Form.PlainText value="Product Name" />
            </FormCol>
            <FormCol cols={{ xs: 24, md: 3 }}>
              <Form.PlainText value="Code" />
            </FormCol>
            <FormCol cols={{ xs: 24, md: 4 }}>
              <Form.PlainText value="Gross" />
            </FormCol>
            <FormCol cols={{ xs: 24, md: 4 }}>
              <Form.PlainText value="Selling Price" />
            </FormCol>
            <FormCol cols={{ xs: 24, md: 4 }}>
              <Form.PlainText value="Cost" />
            </FormCol>
          </FormRow>
        </Form.Col>
      )}
      {(orderedAmProducts || []).map((product, i) => {
        const { item_name, product_code, item_selling_price, item_cost, product_cost, lock_cost, item_gross } = product;
        // NOTE: use product_cost if locked otherwise item_cost
        const costName = lock_cost ? `aryDealerAMFinance[${i}].product_cost` : `aryDealerAMFinance[${i}].item_cost`;
        const defaultCost = lock_cost ? product_cost : item_cost;
        return item_selling_price ? (
          <Fragment key={i}>
            <Form.Col>
              <FormRow className="flex-md-nowrap">
                <FormCol cols={{ xs: 24, md: 8 }} className={styles.labelCol}>
                  <Form.Label>{item_name}</Form.Label>
                </FormCol>
                <FormCol cols={{ xs: 24, md: 3 }}>
                  <Form.PlainText name={`aryDealerAMFinance[${i}].product_code`} value={product_code} />
                </FormCol>
                <FormCol cols={{ xs: 24, md: 4 }}>
                  <Form.PlainText name={`aryDealerAMFinance[${i}].item_gross`} schema="dollars" value={item_gross} />
                </FormCol>
                <FormCol cols={{ xs: 24, md: 4 }}>
                  <Form.PlainText
                    name={`aryDealerAMFinance[${i}].item_selling_price`}
                    value={item_selling_price}
                    schema="dollars"
                  />
                </FormCol>
                <FormCol cols={{ xs: 24, md: 4 }}>
                  <Form.PlainText name={costName} value={defaultCost} schema="dollars" />
                </FormCol>
              </FormRow>
            </Form.Col>
          </Fragment>
        ) : null;
      })}
      {amProducts.length && <Form.PlainText label="Total AM Gross" schema="dollars" value={amProductsTotalGross} />}
      <Form.PlainText label="Time AM Gross Entered" value={amGrossTime} />
      {(orderedFiProducts || []).map((product, fiProductsIndex) => {
        const { item_name, item_selling_price } = product;
        const i = fiProductsIndex + amProducts.length;
        return item_selling_price ? (
          <Fragment key={i}>
            <Form.Col>
              <FormRow className="flex-md-nowrap">
                <FormCol cols={{ xs: 24, md: 8 }} className={styles.labelCol}>
                  <Form.Label>{item_name}</Form.Label>
                </FormCol>
                <FormCol cols={{ xs: 24, md: 16 }}>
                  <Form.PlainText
                    name={`aryDealerAMFinance[${i}].item_selling_price`}
                    schema="dollars"
                    value={item_selling_price}
                  />
                </FormCol>
              </FormRow>
            </Form.Col>
          </Fragment>
        ) : null;
      })}
      <Form.PlainText label="Total F&amp;I Gross" schema="dollars" value={fiProductsTotalGross} />
      <Form.PlainText label="Time F&amp;I Gross Entered" value={fmGrossTime} />
      <Form.PlainText label="Total Backend Gross" schema="dollars" value={totalBackendGross} />
    </Form.Body>
  );
};

// EXPORT
export default function AMFIProducts() {
  return (
    <AMFIProductsProvider>
      <AMFIProductsForm />
    </AMFIProductsProvider>
  );
}
